import { useTranslation } from 'react-i18next';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { globalErrorResponse, isErrorOfType } from '../../shared/notification/actions';
import { invalidateCartSummaryQuery } from './useCartSummaryQuery';
import { invalidateShoppingCartProducts } from './useShoppingCartProducts';
import { invalidateCartGroupedItems } from './useCartGroupedItems';
import { UpdateProductOptions } from '../../store/api';
import { setAmountSingleProductInCart } from './setAmountSingleProductInCart';
import { increaseAmountSingleProductInCart } from './increaseAmountSingleProductInCart';
import { decreaseAmountSingleProductInCart } from './decreaseAmountSingleProductInCart';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { log } from '../../shared/utils/log';
import { useState } from 'react';
import { showNotification } from '../../shared/notification/toastNotification';

const buildChangeAmountProductCallback = (
    product: UpdateProductOptions,
    amount: number,
    command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
): (() => Promise<void>) => {
    const { id: productId, price, subscriptionPeriod } = product;
    const { lendingModel, usageLimit, termLimit } = price ?? {};
    const usageCondition = {
        lendingModel,
        usageLimit,
        termLimit
    };

    const body = {
        usageCondition,
        subscriptionPeriodType: subscriptionPeriod?.type
    };

    return () => {
        switch (command) {
            case ACTION_DECREASE:
                return decreaseAmountSingleProductInCart({ body, productId });
            case ACTION_INCREASE:
                return increaseAmountSingleProductInCart({ body, productId });
            case ACTION_SET:
                return setAmountSingleProductInCart({ body: { ...body, targetAmount: amount }, productId });
        }
    };
};

export const useChangeAmountSingleProductInCart = () => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        changeAmount: async (
            product: UpdateProductOptions,
            amount: number,
            command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
        ) => {
            setLoading(true);
            const apiCallback = buildChangeAmountProductCallback(product, amount, command);
            try {
                await apiCallback();
                showNotification(t('notifications.copyAmountChangeCartBulk.message'));
                invalidateCartSummaryQuery().then();
                invalidateShoppingCartProducts().then();
                invalidateCartGroupedItems().then();
                invalidateCartAggregations().then();
            } catch (error) {
                log.error(error, { causedBy: 'useChangeAmountSingleProductInCart' });

                if (isErrorOfType(error)) {
                    globalErrorResponse(error);
                }
            } finally {
                setLoading(false);
            }
        }
    };
};
